import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, MessageCircle } from "lucide-react";
import { services } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { openWhatsApp } from "@/lib/whatsapp";

export function ServiceCard({ service, index = 0 }) {
  const { lang } = useLang();
  const Icon = Icons[service.icon] || Icons.Truck;
  const copy = service[lang];
  const number = String(index + 1).padStart(2, "0");

  const waMessage = () => {
    const title = copy.title;
    if (lang === "en") {
      if (service.action === "ritiro") return "Hi EMME SERVICE CAR, I'd like to request pickup of an end-of-life vehicle.";
      if (service.action === "transport") return `Hi EMME SERVICE CAR, I'd like to request a transport: ${title}.`;
      return `Hi EMME SERVICE CAR, I need assistance for: ${title}.`;
    }
    if (service.action === "ritiro") return "Ciao EMME SERVICE CAR, vorrei richiedere il ritiro di un veicolo da rottamare.";
    if (service.action === "transport") return `Ciao EMME SERVICE CAR, vorrei richiedere un trasporto: ${title}.`;
    return `Ciao EMME SERVICE CAR, ho bisogno di assistenza per: ${title}.`;
  };

  const ctaCls =
    "mt-5 inline-flex w-fit items-center justify-center gap-2 rounded-md bg-amber/10 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-amber transition-colors hover:bg-amber hover:text-carbon active:scale-95";

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      data-testid={`service-card-${service.id}`}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-edge bg-asphalt transition-colors duration-300 hover:border-amber"
    >
      <div className="relative h-44 overflow-hidden">
        <img
          src={service.image}
          alt={copy.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/30 to-transparent" />
        <span className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-md border border-edge bg-carbon/80 text-amber backdrop-blur-sm">
          <Icon className="h-5 w-5" strokeWidth={2} />
        </span>
        <span className="absolute right-4 top-3 font-display text-sm font-bold text-ice/70">{number}</span>
      </div>

      <div className="flex flex-1 flex-col p-5">
        {copy.badge && (
          <span className="mb-2 inline-flex w-fit items-center gap-1.5 rounded-full border border-amber/40 bg-amber/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber">
            {copy.badge}
          </span>
        )}
        <h3 className="font-display text-lg font-bold text-ice">{copy.title}</h3>
        {/* flatbed line slides in on hover */}
        <span className="mt-2 block h-px w-8 origin-left scale-x-100 bg-amber transition-all duration-300 group-hover:w-16" />
        <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">{copy.desc}</p>
        {copy.note && <p className="mt-2 text-xs text-steel/70">{copy.note}</p>}

        {service.action === "quote" ? (
          <a href="#preventivo" data-testid={`service-cta-${service.id}`} className={ctaCls}>
            {copy.cta} <ArrowRight className="h-4 w-4" />
          </a>
        ) : (
          <button type="button" onClick={() => openWhatsApp(waMessage())} data-testid={`service-cta-${service.id}`} className={ctaCls}>
            {copy.cta} <MessageCircle className="h-4 w-4" />
          </button>
        )}
      </div>
    </motion.article>
  );
}

export function Services() {
  const { t } = useLang();
  return (
    <section id="servizi" data-testid="services-section" className="relative bg-carbon py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <Overline>{t.services.overline}</Overline>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ice sm:text-4xl lg:text-5xl">
            {t.services.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-steel">{t.services.subtitle}</p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
